
import { ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const FaangInterviewContent = () => {
  return (
    <>
      <p className="lead">
        Cracking a machine learning interview at companies like Facebook, Google, Netflix, Snap etc. really comes down to 
        nailing few patterns that these companies look for. In this article, I plan to share my experience interviewing with 
        these companies and also how I went about preparing.
      </p>
      
      <h2>About me:</h2>
      <p>
        I am currently a Principle Machine learning engineer at Roblox in their Personalization team. Previously I worked at Meta. 
        I have been part of two teams at Meta. I was an early ML engineer in the Misinformation modeling team, working on hoax 
        classification models. After 3 years at Misinformation, I moved to the Instagram Ads ranking team where I was part of the 
        core-optimization team building and improving upon Ads ranking models across all surfaces of Instagram (Feed, Stories, Reels, etc).
      </p>

      <h2>My interview experiences</h2>
      <p>
        I've been on the job market for the past two months and attended ~10 onsite interviews and have 6 offers from Google and a few 
        others for ML roles. From among my offers, I decided to join Roblox in their Personalization ML team. This was my first job 
        search attempt since graduating with my Master's almost 6 years ago and I had to learn the landscape of ML interviews to go 
        about strategizing my preparation.
      </p>
      <p>
        In this article, I will share tips and tricks that could help you prepare for MLE interviews (L4 and above). I've attended 
        interviews at Netflix, Google, Snap, Airbnb, Instacart, Doordash, Nextdoor and I'm going to focus this article based on my 
        experience interviewing with these companies.
      </p>
      <p>
        Machine learning interviews are quite different from vanilla software engineering interviews and the ecosystem is evolving to make 
        these interviews more calibrated and structured. But here are things that you can broadly expect:
      </p>
      <ul>
        <li><strong>Phone screen:</strong> Machine learning technical assessment + Leetcode style coding question</li>
        <li><strong>Onsite interview:</strong>
          <ul>
            <li>Machine learning theory round</li>
            <li>Machine learning system design</li>
            <li>Machine learning coding</li>
          </ul>
        </li>
      </ul>

      <h2>1) Machine learning technical assessment</h2>
      <p>
        Typically, big-tech companies have a 1-hour technical phone screen that comprises 1 leetcode style question followed by 10 to 15 mins of 
        rapid-fire ML tech questions towards the end. Some of these questions include:
      </p>
      <ul>
        <li>Explain overfitting and regularization</li>
        <li>Explain the bias-variance tradeoff.</li>
        <li>Explain Gradient descent and Stochastic gradient descent. Which one would you prefer?</li>
        <li>[Difficult] Can you explain logistic regression and derive gradient descent for Logistic regression</li>
        <li>[Difficult] What do eigenvalues and eigenvectors mean in PCA</li>
        <li>[Difficult] Explain different types of Optimizers - How is Adam optimizer different from Rmsprop?</li>
        <li>[Difficult] What are the different types of activation functions and explain about vanishing gradient problem</li>
      </ul>
      
      <h3>How should you prepare for this round</h3>
      <p>
        This round is quite crucial because it tests your basic understanding of common topics in ML. Instead of approaching these questions by 
        studying a cheat sheet, I'd recommend spending a week or two going over the basics from a textbook. I highly recommend this textbook and 
        this Coursera course which you can watch in 1.5X speed. Often, even textbooks don't do a good job of explaining nuanced concepts like 
        Optimizers. In this case, picking up good articles on Medium.com was something that was very useful for my prep. For example, here's a 
        great article that explains different types of optimizers and does a much better job than the textbooks I've read. Aim to spend around 2 
        weeks to a month revising Machine learning basics.
      </p>

      <h2>Onsite - Machine Learning Theory Round</h2>
      <p>
        This round is quite similar to the above technical assessment round and has 45 min to 1 hour dedicated to it. The objective of this round 
        is more to test your waters and distinguish between an ML modeling engineer and vs. ML Infra engineer (Often it is not quite easy to make 
        that distinction from the resume). You can expect two kinds of questions. For example:
      </p>
      
      <h3>Straightforward theory questions:</h3>
      <ul>
        <li>What do L1 and L2 regularization mean and when would you use L1 vs. L2? Can you use both?</li>
        <li>When there are highly correlated features in your dataset, how would the weights for L1 and L2 end up being?</li>
      </ul>

      <h3>Nuanced derivation-based questions that requires a pen and paper:</h3>
      <ul>
        <li>Can you use MSE for evaluating your classification problem instead of Cross entropy</li>
        <li>How does the loss curve for Cross entropy look?</li>
        <li>What does the "minus" in cross-entropy mean?</li>
        <li>Explain how Momentum differs from RMS prop optimizer?</li>
      </ul>

      <p>
        Aim to spend the bulk of your preparation going back to basics and getting the concepts right. I spent &gt;2 weeks revising machine learning 
        concepts. Getting the derivations practiced with pen and paper makes a huge difference. While textbooks are a good source to know how to 
        answer theory questions, if your understanding needs a little bit more work - Youtube videos are a game changer. 50% of my ML theory prep 
        were on Youtube videos, and 50% were on the textbook (more for perfecting the answers). Here's my youtube playlist that I keep going back 
        to, the evening before my onsite interviews.
      </p>

      <h2>Onsite Interview - How to tackle the ML system design round?</h2>
      <p>
        Now, this is the round that few companies manage to conduct well. Finding calibrated interviewers and having the right set of rubrics for 
        evaluating candidates is quite challenging for this round. Some of the companies I interviewed at like Google, Meta, Airbnb, and Doordash 
        had a mature process for ML System design.
      </p>
      <p>
        For ML Design, the expectation from candidates is often not clear and I've found interviewers asking questions that are quite large in scope, 
        leaving little room for going in depth. So it is crucial that candidates remember to clarify expectations by asking questions like:
      </p>
      <ul>
        <li>Do we care about Model deployment and the system design of how ML infra works?</li>
        <li>Do we care about going deep into modeling choices?</li>
        <li>What are the different business objectives that we want to optimize and why do we care about them?</li>
      </ul>

      <p>
        Its also a game changer for candidates to assert and establish what you plan to cover at the outset, so there's minimal interruptions, for e.g
      </p>
      <p>
        I always say at the start "Here's the structure I plan to follow [I jot down 4–5 bullet points], please let me know if you'd like me to cover 
        these in a different order or touch on any of this more than the others".
      </p>

      <p>
        L4 candidates don't typically have an ML design round, for L5 you can expect 1–2 rounds and for L6 there's a minimum of 2 rounds expected.
      </p>
      <p>
        Expect questions like
      </p>
      <ul>
        <li>Design a feed recommendation system</li>
        <li>Design Youtube(Google)</li>
        <li>Design Google contact ranking(Google)</li>
        <li>Design an item replacement recommendation(Instacart)</li>
        <li>Design an ML System to optimize coupon distribution with a set budget(Netflix).</li>
      </ul>

      <h3>Here are top tips for nailing this round</h3>
      <p>
        Have a good pre-defined structure for the interview. Here's my recommended structure:
      </p>

      <p>
        Have a good script for answering any ML Design question. Pretty much every ML design question fits into the above structure and the script can also 
        be somewhat generalized. Here's my pdf [Credit: Hesam Salehian] on how I approach recommendation systems; You can also rely on online machine 
        learning design resources like this course for help with different types of design questions. I've shared more templates and case studies here.
      </p>
      
      <h3>How to salvage a derail?</h3>
      <p>
        Say you are 10 mins into the design interview and the interviewer asks you a question on Business metrics and you spend an excessive amount of 
        time in that section; Now, once you are done - you are unsure of how to proceed? Should you spend more time on metrics? Should you go back to 
        your original structure etc. I've found myself in this situation quite a few times and I've seen the interview derail from there. I often look 
        for more guidance from the interviewer here - so If you are like me - how do you salvage your interview?
      </p>
      <p>
        This is something I noticed far too often especially in ML Design interviews. This is often a function of your interviewer having a pre-defined 
        structure in their mind that collides with your structure. for e.g, If you start speaking about online metrics and they want to know about 
        Training data - Take a pause, make it explicit to the interviewer that this is the structure you intend to follow, and get back on track.
      </p>
      <p>
        Design interviews should typically be led by the candidate with minimal interruptions from the interviewer. So, take ownership here fully knowing 
        that the interviewer on the other end is probably not calibrated and hence it is entirely up to you to utilize your 45 mins to give as much signal 
        as possible.
      </p>
      <p>
        It is also quite possible that the interviewer would want to probe for more signals in a specific area. In which case, ask explicitly "Should I go 
        deep into explaining the modeling section now or would you want me to just touch on it" → Often you'd get a very objective answer which will throw 
        light on what is the interviewer expecting from you.
      </p>

      <p>
        The best way to prepare for ML design rounds is to take as many mock interviews as possible. I took 5 ML mock interviews with my friends which really 
        helped shape my run-book for this round. This brings me to the end of Part 1. In Part 2, I deep dive into types of ML Design questions and the rubric 
        that differentiates an L4 candidate vs. L5 and L6 (Meta levels). 
      </p>
      
      <div className="mt-8 border-t pt-6">
        <div className="flex justify-between items-center mb-4">
          <Link to="/articles/faang-ml-interviews-part2" className="text-blue-600 hover:text-blue-800 flex items-center gap-2">
            Read Part 2: Cracking ML Interviews at FAANG - Pointers for Junior/Senior Staff <ExternalLink size={16} />
          </Link>
        </div>
        
        <p className="mt-4">
          If you found my article useful for your interview prep, Please do support me by subscribing, liking this article. 
          I offer ML system design mock interviews.
        </p>
        
        <p className="text-sm mt-4">
          I'd like to thank my mentors Hesam Salehian, Zakaria Haque and colleagues Abhishek Shah, Srinivas Govindan from Meta.
        </p>
      </div>
    </>
  );
};

export default FaangInterviewContent;
